// TODO: use zod instead of joi
import * as j from 'joi'
import {
  ChargingMode,
  ChargingSchedule,
  EebusChargingStation,
  HeatpumpIo,
  Htc320hIo,
  InstallationFormModel,
  IoInterfaces,
  ModbusTcpChargingStation,
  MyPvIo,
  StorageIo,
  StorageSystem,
} from '../../installation/forms'
import { TFunction } from 'i18next'
import { countStringBytes, doesStringContainEmojis, calculateMinimumMinCurrentSunModeValue } from '../utils'
import { getTreatHtcAsNonSgReady } from '../../installation/utils'
import { PV_METER_MIN_PEAK_POWER, PV_METER_MAX_PEAK_POWER, MAX_TOTAL_POWER_CONSUMPTION } from '../constants'
import { asciiRegex } from './validation'
import { Meter } from '../powermeter'
import { calculateFeedInMaxPower } from '../storage/utils/feed-in-max-power'
import { MAXIMUM_PEAK_POWER } from '../storage/const'

/**
 * Schema object that contains the basic validations for the form fields.
 *
 * More complex validations are handled in their respective components.
 *
 * Common use cases for this schema:
 * - Make a string required.
 * - Make a TimePicker required to prevent invalid times. (Invalid times will fallback to empty strings. Implementation in TimeInput component.)
 * - Number max number
 * - Number min number (when min is higher than 0)
 * - Number min, max based on sibling value.
 */
export const installationSchema = (t: TFunction): j.ObjectSchema<InstallationFormModel> =>
  j.object<InstallationFormModel>({
    storageDevices: j.array().items(
      j
        .object({
          name: j.string().required().custom(asciiValidation),
        })
        .allow(null)
    ),
    generalSettings: j.object<InstallationFormModel['generalSettings']>({
      gridSettings: j.object({
        mainProtection: j.number().max(3000),
        thermalDerating: j.number().max(j.ref('mainProtection', { render: true })),
      }),
      location: j
        .object<InstallationFormModel['generalSettings']['location']>({
          street: j.string().allow(''),
          houseNumber: j.string().allow(''),
          postalCode: j.string().allow(''),
          location: j.string().allow(''),
        })
        .custom((value, helpers) => {
          const fields = ['street', 'houseNumber', 'postalCode', 'location']
          const filled = fields.filter(f => value[f] && value[f].trim() !== '')

          if (filled.length > 0 && filled.length < fields.length) {
            return helpers.error('incompleteLocation')
          }

          return value // valid
        }, 'all-or-none location validation'),
    }),
    storageSystem: j.object<StorageSystem>({
      generalSettings: j.object<StorageSystem['generalSettings']>({
        systemRegulation: j.object<StorageSystem['generalSettings']['systemRegulation']>({
          peakPower: j.number().max(MAXIMUM_PEAK_POWER),
          feedInRatio: j.number().max(100),
          feedInMaxPower: j
            .number()
            .max(j.ref('feedInRatio', { adjust: feedInRatio => calculateFeedInMaxPower(MAXIMUM_PEAK_POWER, feedInRatio || 0), render: true })),
        }),
        advancedSettings: j.object<StorageSystem['generalSettings']['advancedSettings']>({
          emergencyPower: j.object<StorageSystem['generalSettings']['advancedSettings']['emergencyPower']>({
            emergencyPowerDelay: j.number().max(60),
          }),
          chargeBlockingWeeklySchedule: j.array().items(
            j.object({
              fromTime: j.date().required(),
              toTime: j.date().required(),
            })
          ),
          dischargeBlockingWeeklySchedule: j.array().items(
            j.object({
              fromTime: j.date().required(),
              toTime: j.date().required(),
            })
          ),
        }),
      }),
      storageSpecificSettings: j.array().items(
        j.object<StorageSystem['storageSpecificSettings'][number]>({
          advancedSettings: j.object<StorageSystem['storageSpecificSettings'][number]['advancedSettings']>({
            batteryOperationRange: j.object<StorageSystem['storageSpecificSettings'][number]['advancedSettings']['batteryOperationRange']>({
              maximumChargingPower: j.when('isStorageSingleBattery', {
                is: true,
                then: j.number().max(3000),
                otherwise: j.number().max(4500),
              }),
              maximumDischargingPower: j.when('isStorageSingleBattery', {
                is: true,
                then: j.number().max(3000),
                otherwise: j.number().max(4500),
              }),
            }),
            emergencyPower: j.object<StorageSystem['storageSpecificSettings'][number]['advancedSettings']['emergencyPower']>({
              emergencyPowerReserve: j.number().max(20_000),
            }),
          }),
        })
      ),
    }),
    chargingStations: j.object({
      modbusTcp: j.array().items(
        j.object<ModbusTcpChargingStation>({
          name: j.string().required().custom(asciiValidation),
          protection: j.number().min(8).max(64).less(j.ref('/generalSettings.gridSettings.mainProtection')),
          holdingTime: j.number().min(1).max(60),
          minCharge: j
            .number()
            .min(6)
            .max(j.ref('protection', { adjust: protection => protection - 2 })),
          ledIntensityPercentage: j.number().min(30).max(100),
          fallbackMaxCurrent: j
            .number()
            .min(0)
            .invalid(1, 2, 3, 4, 5)
            .max(j.ref('protection'))
            .messages({
              'any.invalid': t('installation.evseEditor.fallback-max-current-forbiddenValue', {
                low: '1A',
                high: '5A',
              }),
            }),
          carAtHomeBehavior: j.object<ModbusTcpChargingStation['carAtHomeBehavior']>({
            chargingPointOperatorWs: j.when('isEnabled', {
              is: true,
              then: j
                .string()
                .max(250)
                .custom((value, helpers) => {
                  const lowerCaseValue = String(value).toLowerCase()
                  if (!(lowerCaseValue.startsWith('ws://') || lowerCaseValue.startsWith('wss://'))) return helpers.error('startsWithWss')

                  if (!/^[A-Za-z0-9/:?#[\]@!$&%'(/)*+,;=.\-_]*$/.test(value)) return helpers.error('invalidOcppUrl')

                  return value
                }),
            }),
            ocppId: j.when('isEnabled', {
              is: true,
              then: j
                .string()
                .max(64)
                .allow('')
                .custom(asciiValidation)
                .custom((value, helper) => {
                  if (countStringBytes(value) >= 64 || doesStringContainEmojis(value)) {
                    return helper.error('invalidOcppId')
                  }
                  return value
                }),
            }),
          }),
          solarRatio: j
            .number()
            .min(0)
            .min(
              j.ref('minCharge', {
                adjust: calculateMinimumMinCurrentSunModeValue,
                render: true,
              })
            )
            .max(100),
          chargingMode: j.string().valid(...ChargingMode),
          safePvSchedule: j.when('chargingMode', {
            is: 'pvImmediate',
            then: j.array().items(
              j.object<ChargingSchedule[number]>({
                energyWanted: j.number().min(0.1).max(300),
              })
            ),
          }),
          efficientPvSchedule: j.when('chargingMode', {
            is: 'pvDelayed',
            then: j.array().items(
              j.object<ChargingSchedule[number]>({
                departureTime: j.string().required(),
                energyWanted: j.number().min(0.1).max(300),
              })
            ),
          }),
        })
      ),
      eebus: j.array().items(
        j.object<EebusChargingStation>({
          name: j.string().required().custom(asciiValidation),
          protection: j.number().min(8).max(64).less(j.ref('/generalSettings.gridSettings.mainProtection')),
          minCharge: j
            .number()
            .min(6)
            .max(j.ref('protection', { adjust: protection => protection - 2 })),
          holdingTime: j.number().min(1).max(60),
          solarRatio: j
            .number()
            .min(0)
            .min(
              j.ref('minCharge', {
                adjust: calculateMinimumMinCurrentSunModeValue,
                render: true,
              })
            )
            .max(100),
          chargingMode: j.string().valid(...ChargingMode),
          safePvSchedule: j.when('chargingMode', {
            is: 'pvImmediate',
            then: j.array().items(
              j.object<ChargingSchedule[number]>({
                energyWanted: j.number().min(0.1).max(300),
              })
            ),
          }),
          efficientPvSchedule: j.when('chargingMode', {
            is: 'pvDelayed',
            then: j.array().items(
              j.object<ChargingSchedule[number]>({
                departureTime: j.string().required(),
                energyWanted: j.number().min(0.1).max(300),
              })
            ),
          }),
        })
      ),
    }),
    heatpump: j
      .object<HeatpumpIo>({
        name: j.string().required().custom(asciiValidation),
        nominalPower: j.number().min(1).max(23000),
      })
      .allow(null),
    meters: j.array().items(
      j.object<Meter>({
        name: j.string().required().custom(asciiValidation),
        peakPower: j.number().min(PV_METER_MIN_PEAK_POWER).max(PV_METER_MAX_PEAK_POWER).allow(null),
      })
    ),
    ioInterfaces: j.object<IoInterfaces>({
      htc320h: j
        .object<Htc320hIo>({
          name: j.string().required().custom(asciiValidation),
          totalPowerConsumption: j.when('isConsumptionActive', {
            is: true,
            then: j.number().min(0).max(MAX_TOTAL_POWER_CONSUMPTION),
          }),
          nominalPower: j
            .number()
            .max(23_000)
            .when(
              j.ref('/', {
                adjust: installation => {
                  if (!installation) return false
                  const { generalSettings, heatpump, ioInterfaces } = installation
                  return getTreatHtcAsNonSgReady(generalSettings, heatpump, ioInterfaces.storage)
                },
              }),
              { is: false, then: j.number().min(1) }
            ),
        })
        .allow(null),
      storage: j
        .object<StorageIo>({
          name: j.string().required().custom(asciiValidation),
          nominalPower: j.number().min(1).max(23_000),
        })
        .allow(null),
      mypv: j
        .object<MyPvIo>({
          name: j.string().required().custom(asciiValidation),
          boostConfig: j.array().items(
            j.object({
              securityTemperature: j.number().min(0).max(90),
              fromTime: j.date().required(),
              toTime: j.date().required(),
            })
          ),
        })
        .allow(null),
    }),
  })

const asciiValidation: j.CustomValidator<unknown, unknown> = (value, helpers) => {
  const isAscii = asciiRegex.test(String(value))

  if (!isAscii) return helpers.error('notAscii')

  return value // valid
}
